import { useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Import useParams & useNavigate
import { Formik, Form, Field } from "formik"; // Import Field from Formik
import * as Yup from "yup";
import BuildersLogo from "@components/BuildersLogo";
// import SectionHeading from "@components/SectionHeading";
import FormikInput from "../../components/form/FormikInput";
import HeadlessButton from "@components/Button";
import Spinner from "@components/Spinner";
import { useTranslation } from "react-i18next";
import LanguageToggle from "../../components/constant_components/LanguageToggle";
import { login, resetpassword } from "../../service/authService"; // Import login and resetpassword function
import { useAuth } from "@components/context/AuthContext"; // Import AuthContext for authentication state
import { SnackbarContext } from "../../components/constant_components/providers/SnackbarProvider";
import axiosClient from "@service/axiosClient";

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { type } = useParams(); // Retrieve user type (admin, user, etc.)
  const navigate = useNavigate(); // Navigation hook
  const { setUser, handleInfo } = useAuth(); // Get handlesetUser from AuthContext

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [showContractorId, setShowContractorId] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const { triggerSnackbar } = useContext(SnackbarContext);

  useEffect(() => {
    // Set page title based on user type (admin, user, etc.)
    document.title =
      type === "admin" ? t("login.adminTitle") : t("login.userTitle");
  }, [type, t]);

  useEffect(() => {
    // Simulate page load delay
    const timer = setTimeout(() => setIsPageLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Define initial form values
  const initialValues = {
    email: "",
    password: "",
    contractor_id: "",
  };

  // Define form validation schema using Yup
  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t("login.email.invalid"))
      .required(t("login.email.required")),
    password: Yup.string().required(t("login.password.required")),
    contractor_id: showContractorId
      ? Yup.string().required(t("login.contractor_id.required"))
      : Yup.string(),
  });

  // Handle forgot password
  const handleForgotPassword = async (email) => {
    if (!email) {
      triggerSnackbar(
        t("login.email.required") || "Please enter your email address"
      );
      return;
    }

    setIsResettingPassword(true);
    try {
      const language = i18n.language === "es" ? "S" : "E";
      await resetpassword({ email, language }, type.toLowerCase());
      triggerSnackbar(
        t("login.resetPassword.success") || "Password reset email has been sent"
      );
    } catch (error) {
      triggerSnackbar(
        error.msg ||
          t("login.resetPassword.error") ||
          "Failed to send reset email"
      );
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Handle form submission for login
  const handleSubmit = async (values) => {
    setIsLoading(true);

    try {
      const userData = await login(values, type.toLowerCase());
      setUser(userData);

      let data = {
        role: "user",
        plants: [],
        email: values.email,
      };
      // if (type === "sadmin" || type === "admin" || type === "user") {
      try {
        const response = await axiosClient.get("/auth/info/admin");
        data = {
          userType: response.data.userType,
          plants: response.data.plants,
          roles: response.data.roles,
          permissions: response.data.permissions,
          user_id:
            response.data.userType === "user"
              ? response.data.user_id
              : response.data.admin_id,
          user_name:
            response.data.userType === "user"
              ? response.data.user_name
              : response.data.admin_name,
          worker_id: response.data.worker_id,
        };
        handleInfo(data);
      } catch (error) {
        if (
          error.response?.data?.msg === "Worker not found" &&
          type === "user"
        ) {
          if (showContractorId) {
            await axiosClient.post("/users/send_temp_code", {
              user_id: userData.id,
              contractor_id: values.contractor_id,
            });
            const userDataWithOrigin = {
              ...values,
              origin: "login_page",
            };
            sessionStorage.setItem(
              "pending_user_data",
              JSON.stringify(userDataWithOrigin)
            );
            navigate(`/confirm/user/${userData.id}/${values.contractor_id}`);
            setIsLoading(false);
            return;
          }
          triggerSnackbar(t("login.contractor_id.required"));
          setShowContractorId(true);
          return;
        }
        triggerSnackbar(error.msg || t("login.error"));
        setIsLoading(false);
        return;
      }
      // }

      if (type === "admin") {
        navigate(`/hours-upload`);
      } else if (type === "sadmin") {
        navigate(`/sadmin/landing`);
      } else if (type === "user") {
        navigate(`/contractor/${userData.id}`);
      } else {
        navigate(`/logout`);
      }
    } catch (error) {
      triggerSnackbar(error.msg || t("login.error"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-100 tw-relative">
      {/* Language Toggle in the Top-Right Corner */}
      <div className="tw-absolute tw-top-4 tw-right-4">
        <LanguageToggle />
      </div>

      {isPageLoading ? (
        <div className="tw-flex tw-flex-col tw-items-center">
          <BuildersLogo />
          <div className="tw-mt-6 tw-w-[80%] md:tw-w-[30%]">
            <div className="tw-h-12 tw-bg-neutral-200 tw-animate-pulse tw-rounded-md"></div>
          </div>
        </div>
      ) : (
        <div className="tw-w-[90%] tw-max-w-lg tw-bg-white tw-p-6 tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-300">
          <div className="tw-flex tw-justify-center tw-mb-4">
            <BuildersLogo />
          </div>

          <h2 className="tw-text-2xl tw-text-center tw-text-primary-light tw-font-bold tw-mb-6">
            {type === "admin"
              ? t("login.adminTitle")
              : type === "user"
              ? t("login.userTitle")
              : t("login.sadminTitle")}
          </h2>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, values }) => {
              return (
                <Form>
                  <div className="tw-mb-4">
                    <Field name="email">
                      {({ field, form }) => (
                        <FormikInput
                          {...field}
                          name="email"
                          label={t("login.email.label")}
                          type="email"
                          placeholder={t("login.email.placeholder")}
                          error={
                            form.touched.email &&
                            typeof form.errors.email === "string"
                              ? form.errors.email
                              : ""
                          }
                        />
                      )}
                    </Field>

                    <Field name="password">
                      {({ field, form }) => (
                        <FormikInput
                          {...field}
                          name="password"
                          label={t("login.password.label")}
                          type="password"
                          placeholder={t("login.password.placeholder")}
                          error={
                            form.touched.password &&
                            typeof form.errors.password === "string"
                              ? form.errors.password
                              : ""
                          }
                        />
                      )}
                    </Field>

                    {showContractorId && (
                      <Field name="contractor_id">
                        {({ field, form }) => (
                          <FormikInput
                            {...field}
                            name="contractor_id"
                            label={
                              t("login.contractor_id.label") || "Contractor ID"
                            }
                            type="text"
                            placeholder={
                              t("login.contractor_id.placeholder") ||
                              "Enter your contractor ID"
                            }
                            error={
                              form.touched.contractor_id &&
                              typeof form.errors.contractor_id === "string"
                                ? form.errors.contractor_id
                                : ""
                            }
                          />
                        )}
                      </Field>
                    )}
                  </div>

                  <div className="tw-flex tw-justify-center">
                    <HeadlessButton
                      type="submit"
                      disabled={isLoading}
                      className="tw-w-full tw-bg-primary-light tw-text-white tw-py-2 tw-rounded-md tw-font-semibold"
                    >
                      {isLoading ? (
                        <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                          {t("login.processing")}
                          <Spinner />
                        </div>
                      ) : (
                        t("login.button")
                      )}
                    </HeadlessButton>
                  </div>

                  {(type === "admin" || type === "user") && (
                    <div className="tw-text-right tw-mt-4">
                      <button
                        type="button"
                        onClick={() => handleForgotPassword(values.email)}
                        disabled={isResettingPassword}
                        className="tw-text-sm tw-text-primary-light tw-hover:underline tw-disabled:opacity-50"
                      >
                        {isResettingPassword ? (
                          <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                            {t("login.resetPassword.sending") || "Sending..."}
                            <Spinner />
                          </div>
                        ) : (
                          t("login.resetPassword.link") || "Forgot Password?"
                        )}
                      </button>
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>

          {/* Later for Need Help page route */}
          {/* <div className="tw-text-center tw-mt-4 tw-text-sm tw-text-gray-500">
            <a href="#help" className="tw-hover:underline">
              {t("login.help")}
            </a>
          </div> */}
        </div>
      )}
    </div>
  );
}
